import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';

import { Box, Button, COLORS, FontWeight, Input, Modal, Text, UnstyledButton } from '@clutter/clean';

import { Currency } from '@shared/components/helpers';
import { useDebounce } from '@shared/hooks';

import { Tabs } from '@portal/components/helpers/tabs';
import { DisposalPricingSummary } from '@portal/components/orders/disposal_confirmation/pricing_modal';
import { RestrictedDisposalItems } from '@portal/components/orders/home/disposal_restrictions';
import {
  AppointmentHubDocument,
  Disposal__CategoryGroupKindEnum,
  OrderDetailsDocument,
  Status,
  useAttachDisposalMutation,
  useCancelOrderMutation,
  useDisposalCategorySearchQuery,
  useDisposalEstimatePricingQuery,
  useDisposalPricingInfoQuery,
} from '@portal/schema';

// SVG Icons for each disposal group
const FurnitureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
    <path
      d="M4.33333 19.5C4.02639 19.5 3.7691 19.3962 3.56146 19.1885C3.35382 18.9809 3.25 18.7236 3.25 18.4167V17.3333C2.34722 17.3333 1.57986 17.0174 0.947917 16.3854C0.315972 15.7535 0 14.9861 0 14.0833V8.66667C0 7.76389 0.315972 6.99653 0.947917 6.36458C1.57986 5.73264 2.34722 5.41667 3.25 5.41667V3.25C3.25 2.34722 3.56597 1.57986 4.19792 0.947917C4.82986 0.315972 5.59722 0 6.5 0H17.3333C18.2361 0 19.0035 0.315972 19.6354 0.947917C20.2674 1.57986 20.5833 2.34722 20.5833 3.25V5.41667C21.4861 5.41667 22.2535 5.73264 22.8854 6.36458C23.5174 6.99653 23.8333 7.76389 23.8333 8.66667V14.0833C23.8333 14.9861 23.5174 15.7535 22.8854 16.3854C22.2535 17.0174 21.4861 17.3333 20.5833 17.3333V18.4167C20.5833 18.7236 20.4795 18.9809 20.2719 19.1885C20.0642 19.3962 19.8069 19.5 19.5 19.5C19.1931 19.5 18.9358 19.3962 18.7281 19.1885C18.5205 18.9809 18.4167 18.7236 18.4167 18.4167V17.3333H5.41667V18.4167C5.41667 18.7236 5.31285 18.9809 5.10521 19.1885C4.89757 19.3962 4.64028 19.5 4.33333 19.5ZM3.25 15.1667H20.5833C20.8903 15.1667 21.1476 15.0628 21.3552 14.8552C21.5628 14.6476 21.6667 14.3903 21.6667 14.0833V8.66667C21.6667 8.35972 21.5628 8.10243 21.3552 7.89479C21.1476 7.68715 20.8903 7.58333 20.5833 7.58333C20.2764 7.58333 20.0191 7.68715 19.8115 7.89479C19.6038 8.10243 19.5 8.35972 19.5 8.66667V13H4.33333V8.66667C4.33333 8.35972 4.22951 8.10243 4.02187 7.89479C3.81424 7.68715 3.55694 7.58333 3.25 7.58333C2.94306 7.58333 2.68576 7.68715 2.47812 7.89479C2.27049 8.10243 2.16667 8.35972 2.16667 8.66667V14.0833C2.16667 14.3903 2.27049 14.6476 2.47812 14.8552C2.68576 15.0628 2.94306 15.1667 3.25 15.1667ZM6.5 10.8333H17.3333V8.66667C17.3333 8.17917 17.4326 7.73681 17.6312 7.33958C17.8299 6.94236 18.0917 6.59028 18.4167 6.28333V3.25C18.4167 2.94306 18.3128 2.68576 18.1052 2.47813C17.8976 2.27049 17.6403 2.16667 17.3333 2.16667H6.5C6.19306 2.16667 5.93576 2.27049 5.72812 2.47813C5.52049 2.68576 5.41667 2.94306 5.41667 3.25V6.28333C5.74167 6.59028 6.00347 6.94236 6.20208 7.33958C6.40069 7.73681 6.5 8.17917 6.5 8.66667V10.8333Z"
      fill="#1C1B1F"
    />
  </svg>
);

const BoxesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
    <path
      d="M8 17.425V10.575L2 7.1V13.95L8 17.425ZM10 17.425L16 13.95V7.1L10 10.575V17.425ZM9 8.85L14.925 5.425L9 2L3.075 5.425L9 8.85ZM1 15.7C0.683333 15.5167 0.4375 15.275 0.2625 14.975C0.0875 14.675 0 14.3417 0 13.975V6.025C0 5.65833 0.0875 5.325 0.2625 5.025C0.4375 4.725 0.683333 4.48333 1 4.3L8 0.275C8.31667 0.0916667 8.65 0 9 0C9.35 0 9.68333 0.0916667 10 0.275L17 4.3C17.3167 4.48333 17.5625 4.725 17.7375 5.025C17.9125 5.325 18 5.65833 18 6.025V13.975C18 14.3417 17.9125 14.675 17.7375 14.975C17.5625 15.275 17.3167 15.5167 17 15.7L10 19.725C9.68333 19.9083 9.35 20 9 20C8.65 20 8.31667 19.9083 8 19.725L1 15.7Z"
      fill="#1C1B1F"
    />
  </svg>
);

const AppliancesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
    <path
      d="M6 18V16H8V14H2C1.45 14 0.979167 13.8042 0.5875 13.4125C0.195833 13.0208 0 12.55 0 12V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V12C20 12.55 19.8042 13.0208 19.4125 13.4125C19.0208 13.8042 18.55 14 18 14H12V16H14V18H6ZM2 12H18V2H2V12Z"
      fill="#1C1B1F"
    />
  </svg>
);

const HouseholdIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M4 18V8L10 3.5L16 8V18H12V12H8V18H4ZM6 16H7V10H13V16H14V9L10 6L6 9V16ZM10 10.45Z" fill="#1C1B1F" />
  </svg>
);

const SportsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M20.57 14.86L22 13.43L20.57 12L17 15.57L8.43 7L12 3.43L10.57 2L9.14 3.43L7.71 2L5.57 4.14L4.14 2.71L2.71 4.14L4.14 5.57L2 7.71L3.43 9.14L2 10.57L3.43 12L7 8.43L15.57 17L12 20.57L13.43 22L14.86 20.57L16.29 22L18.43 19.86L19.86 21.29L21.29 19.86L19.86 18.43L22 16.29L20.57 14.86Z"
      fill="#1C1B1F"
    />
  </svg>
);

/**
 * Icon mapping for disposal group kinds
 * Maps backend group kind enum to React icon components
 */
const DISPOSAL_GROUP_ICONS: Record<Disposal__CategoryGroupKindEnum, React.FC> = {
  [Disposal__CategoryGroupKindEnum.Furniture]: FurnitureIcon,
  [Disposal__CategoryGroupKindEnum.Boxes]: BoxesIcon,
  [Disposal__CategoryGroupKindEnum.Appliances]: AppliancesIcon,
  [Disposal__CategoryGroupKindEnum.Recreational]: SportsIcon,
  [Disposal__CategoryGroupKindEnum.Other]: HouseholdIcon,
};

type CategoryItem = {
  id: string;
  name: string;
  disposalCategoryGroupKind: Disposal__CategoryGroupKindEnum;
};

type DisposalItem = {
  categoryID: string;
  categoryName: string;
  quantity: number;
  disposalCategoryGroupKind: Disposal__CategoryGroupKindEnum;
};

type DisposalGroup = {
  name: string;
  kind: Disposal__CategoryGroupKindEnum;
  position: number;
};

/**
 * Groups categories by their disposal group kind, sorted according to backend-provided ordering
 * @param categories - Array of categories with disposal group information
 * @param groups - Array of group metadata from backend (with ordering and icons)
 * @returns Record of group kinds to category arrays, ordered by backend position
 */
function groupCategoriesByGroup(
  categories: ReadonlyArray<{
    id: string;
    name: string;
    defaultCuft?: number | null;
    disposalMinimumPrice?: number | null;
    disposalCategoryGroupKind: Disposal__CategoryGroupKindEnum;
  }>,
  groups: ReadonlyArray<DisposalGroup>,
): Record<string, CategoryItem[]> {
  // Build initial grouping
  const categoryGroups: Record<string, CategoryItem[]> = {};

  categories.forEach((category) => {
    const groupKind = category.disposalCategoryGroupKind;

    if (!categoryGroups[groupKind]) {
      categoryGroups[groupKind] = [];
    }

    categoryGroups[groupKind].push({
      id: category.id,
      name: category.name,
      disposalCategoryGroupKind: category.disposalCategoryGroupKind,
    });
  });

  // Sort items within each group alphabetically
  Object.keys(categoryGroups).forEach((key) => {
    categoryGroups[key].sort((a, b) => a.name.localeCompare(b.name));
  });

  // Order groups according to backend-provided order
  const orderedGroups: Record<string, CategoryItem[]> = {};
  // Sort groups by position field from backend
  const sortedGroups = [...groups].sort((a, b) => a.position - b.position);

  sortedGroups.forEach((group) => {
    if (categoryGroups[group.kind]) {
      orderedGroups[group.kind] = categoryGroups[group.kind];
    }
  });

  return orderedGroups;
}

// Styled components
const StickySearchBar = styled.div`
  flex-shrink: 0; /* Prevent search bar from being compressed */
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  padding: 16px 24px 8px;
  border-bottom: 1px solid ${COLORS.cloud};
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled(Input)`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid ${COLORS.grayBorder};
  border-radius: 8px;
  font-size: 16px;
`;

const ClearButton = styled(UnstyledButton)<{ visible: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  color: ${COLORS.storm};
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 150ms ease;

  &:hover {
    background-color: ${COLORS.cloud};
  }

  &::before {
    content: '×';
    font-size: 20px;
    line-height: 1;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 24px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Basier Square', sans-serif;
  font-weight: ${FontWeight.Medium};
  font-size: 14px;
  color: ${COLORS.panther};
  margin-bottom: 12px;
`;

const ItemTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ItemTag = styled.div<{ selected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${({ selected }) => (selected ? COLORS.tealBackground : COLORS.cloud)};
  border: 1.5px solid ${({ selected }) => (selected ? COLORS.tealPrimary : COLORS.grayBorder)};
  border-radius: 20px;
  cursor: pointer;
  transition: all 150ms ease;

  &:hover {
    border-color: ${COLORS.tealPrimary};
  }
`;

const ItemName = styled.span`
  font-family: 'Basier Square', sans-serif;
  font-weight: ${FontWeight.Medium};
  font-size: 14px;
  color: ${COLORS.panther};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QuantityButton = styled(UnstyledButton)<{ variant: 'add' | 'remove' }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ variant }) => (variant === 'add' ? COLORS.tealBrand : 'transparent')};
  color: ${({ variant }) => (variant === 'add' ? COLORS.cloud : COLORS.storm)};
  font-size: 14px;
  font-weight: ${FontWeight.Medium};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityText = styled.span`
  font-family: 'Basier Square', sans-serif;
  font-weight: ${FontWeight.Medium};
  font-size: 14px;
  color: ${COLORS.panther};
  min-width: 16px;
  text-align: center;
`;

const ModalContainer = styled(Box.Flex)`
  flex-direction: column;
  min-width: 300px;
  max-height: 85vh;
  width: calc(100vw - 32px);
  max-width: 650px;
  overflow: hidden;

  @media (min-width: 768px) {
    width: 650px; /* Fixed width on desktop to prevent resizing */
  }
`;

const ModalHeader = styled(Box.Flex)`
  flex-shrink: 0; /* Prevent header from being compressed */
  flex-direction: column;
  padding: 40px 24px 0;
  gap: 8px;
`;

const TabsContainer = styled(Box)`
  flex-shrink: 0; /* Prevent tabs from being compressed */
  padding: 0 24px;
`;

const ScrollableContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 24px 16px;
  min-height: 0; /* Allow flex shrinking */
`;

const StickyFooter = styled(Box)`
  flex-shrink: 0; /* Prevent footer from being compressed */
  border-top: 1px solid ${COLORS.cloud};
  background: white;
  padding: 12px 24px 16px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
`;

const CompactSummary = styled.div`
  margin-bottom: 8px;
  padding: 10px 12px;
  background: ${COLORS.tealBackground};
  border-radius: 6px;
  border: 1px solid ${COLORS.tealPrimary};
`;

const TAB_SELECT = 'select';
const TAB_PRICING = 'pricing';
const TAB_RESTRICTIONS = 'restrictions';

type TabType = typeof TAB_SELECT | typeof TAB_PRICING | typeof TAB_RESTRICTIONS;

type ExistingDisposalItem = {
  id: string;
  quantity: number;
  category: {
    id: string;
    name: string;
    defaultCuft?: number | null;
    disposalMinimumPrice?: number | null;
    disposalCategoryGroupKind: Disposal__CategoryGroupKindEnum;
  };
};

export function DisposalAttachModal({
  isOpen,
  parentID,
  disposalID,
  existingItems,
  parentOrderStarted,
  onClose,
}: {
  parentID: string;
  isOpen: boolean;
  disposalID?: string;
  existingItems?: ReadonlyArray<ExistingDisposalItem>;
  parentOrderStarted: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<TabType>(TAB_SELECT);
  const [selectedItems, setSelectedItems] = useState<Map<string, DisposalItem>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');

  // Modal is read-only once order starts (for price estimation only)
  const readOnly = parentOrderStarted;

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch featured categories from backend
  const { data: pricingInfoData, loading: pricingInfoLoading } = useDisposalPricingInfoQuery();
  const featuredCategories = useMemo(
    () => pricingInfoData?.disposalFeaturedItemCategories ?? [],
    [pricingInfoData?.disposalFeaturedItemCategories],
  );

  // Search categories when user types
  const { data: searchData, loading: searchLoading } = useDisposalCategorySearchQuery({
    variables: { query: debouncedSearchQuery },
    skip: !debouncedSearchQuery || debouncedSearchQuery.length === 0,
  });
  const searchResults = useMemo(() => searchData?.categorySearchResults ?? [], [searchData?.categorySearchResults]);

  const isSearching = debouncedSearchQuery && debouncedSearchQuery.length > 0;

  // Determine base categories: search results if searching, featured otherwise
  const baseCategories = useMemo(() => {
    if (isSearching) {
      return searchResults;
    }
    return featuredCategories;
  }, [isSearching, searchResults, featuredCategories]);

  // Final display categories:
  // - When searching: only show search results
  // - When not searching: show featured categories + any selected items
  const displayCategories = useMemo(() => {
    const categoryMap = new Map(baseCategories.map((cat) => [cat.id, cat]));

    // Add selected items only when NOT searching (they stay visible on main screen)
    if (!isSearching) {
      selectedItems.forEach((item) => {
        if (!categoryMap.has(item.categoryID)) {
          categoryMap.set(item.categoryID, {
            id: item.categoryID,
            name: item.categoryName,
            defaultCuft: null, // Not needed for display, backend handles pricing
            disposalMinimumPrice: null, // Not needed for display, backend handles pricing
            disposalCategoryGroupKind: item.disposalCategoryGroupKind,
          });
        }
      });
    }

    return Array.from(categoryMap.values());
  }, [baseCategories, selectedItems, isSearching]);

  const disposalCategoryGroups = useMemo(
    () => pricingInfoData?.disposalPricingInfo?.categoryGroupsMetadata ?? [],
    [pricingInfoData?.disposalPricingInfo?.categoryGroupsMetadata],
  );
  const groupedCategories = useMemo(
    () => groupCategoriesByGroup(displayCategories, disposalCategoryGroups),
    [displayCategories, disposalCategoryGroups],
  );

  // Initialize selected items from existing disposal when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset search-related state
    setSearchQuery('');

    if (existingItems && existingItems.length > 0) {
      // Build selected items from existing items (use category data from backend)
      const map = new Map<string, DisposalItem>();
      existingItems.forEach((item) => {
        map.set(item.category.id, {
          categoryID: item.category.id,
          categoryName: item.category.name,
          quantity: item.quantity,
          disposalCategoryGroupKind: item.category.disposalCategoryGroupKind,
        });
      });
      setSelectedItems(map);
    } else {
      setSelectedItems(new Map());
    }
  }, [isOpen, existingItems]);

  const refetchQueries = [
    { query: AppointmentHubDocument, variables: { orderID: parentID } },
    { query: OrderDetailsDocument, variables: { orderID: parentID } },
  ];

  const [attachDisposal, { loading: attaching, error: attachError }] = useAttachDisposalMutation({
    refetchQueries,
    awaitRefetchQueries: true,
    onCompleted: ({ disposalAttach }) => {
      if (disposalAttach?.status === Status.Ok) {
        onClose();
      }
    },
  });

  const [cancel, { loading: canceling, error: cancelError }] = useCancelOrderMutation({
    variables: { orderID: disposalID!, skipCancellationFee: true },
    refetchQueries,
    awaitRefetchQueries: true,
    onCompleted: ({ cancelOrder }) => {
      if (cancelOrder?.status === Status.Ok) {
        onClose();
      }
    },
  });

  const handleAddItem = useCallback((item: CategoryItem) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(item.id);
      if (existing) {
        newMap.set(item.id, { ...existing, quantity: existing.quantity + 1 });
      } else {
        newMap.set(item.id, {
          categoryID: item.id,
          categoryName: item.name,
          quantity: 1,
          disposalCategoryGroupKind: item.disposalCategoryGroupKind,
        });
      }
      return newMap;
    });
  }, []);

  const handleRemoveItem = useCallback((item: CategoryItem) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(item.id);
      if (existing) {
        if (existing.quantity <= 1) {
          newMap.delete(item.id);
        } else {
          newMap.set(item.id, { ...existing, quantity: existing.quantity - 1 });
        }
      }
      return newMap;
    });
  }, []);

  const handleTagClick = useCallback(
    (item: CategoryItem) => {
      const existing = selectedItems.get(item.id);
      if (existing) {
        // Already selected - do nothing on tag click, use +/- buttons
      } else {
        handleAddItem(item);
      }
    },
    [selectedItems, handleAddItem],
  );

  const selectedItemsArray = useMemo(() => Array.from(selectedItems.values()), [selectedItems]);

  // Prepare category selections for backend pricing query
  const categorySelections = useMemo(
    () =>
      selectedItemsArray.map((item) => ({
        categoryID: item.categoryID,
        quantity: item.quantity,
      })),
    [selectedItemsArray],
  );

  // Fetch pricing from backend - this ensures pricing is always accurate and consistent
  const { data: pricingData, loading: pricingLoading } = useDisposalEstimatePricingQuery({
    variables: { categorySelections },
    skip: categorySelections.length === 0,
  });

  const pricing = useMemo(() => {
    if (categorySelections.length === 0) return null;
    if (!pricingData?.disposalEstimatePricing) return null;

    const estimate = pricingData.disposalEstimatePricing;
    return {
      itemsTotal: estimate.itemsTotal,
      flatFee: estimate.flatFee,
      total: estimate.totalCost,
    };
  }, [pricingData, categorySelections.length]);

  const hasDisposal = !!disposalID;
  const loading = attaching || canceling;
  const error = attachError || cancelError;

  const handleSubmit = async () => {
    const itemsToSend = selectedItemsArray.map((item) => ({
      categoryID: item.categoryID,
      quantity: item.quantity,
    }));

    try {
      await attachDisposal({
        variables: {
          parentID,
          items: itemsToSend,
        },
      });
      // Modal will close via onCompleted callback after refetch queries complete
    } catch (error) {
      // Error is displayed via the error state (attachError)
      console.error('Failed to attach disposal:', error);
    }
  };

  // Categories are already filtered by backend search, just use grouped categories
  const filteredCategories = groupedCategories;

  return (
    <Modal isOpen={isOpen} handleModalClose={onClose} includeCloseButton>
      <ModalContainer>
        {/* Fixed Header */}
        <ModalHeader>
          <Text.Title size="medium">Disposal Service</Text.Title>
        </ModalHeader>

        <TabsContainer>
          <Tabs>
            <Tabs.Tab onClick={() => setTab(TAB_SELECT)} active={tab === TAB_SELECT}>
              Select Items
            </Tabs.Tab>
            <Tabs.Tab onClick={() => setTab(TAB_PRICING)} active={tab === TAB_PRICING}>
              Pricing
            </Tabs.Tab>
            <Tabs.Tab onClick={() => setTab(TAB_RESTRICTIONS)} active={tab === TAB_RESTRICTIONS}>
              Restrictions
            </Tabs.Tab>
          </Tabs>
        </TabsContainer>

        {tab === TAB_SELECT && (
          <>
            {/* Sticky Search Bar */}
            <StickySearchBar>
              <SearchInputContainer>
                <SearchInput
                  placeholder="Add item"
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                />
                <ClearButton
                  visible={searchQuery.length > 0}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                />
              </SearchInputContainer>
            </StickySearchBar>

            {/* Info banner when in read-only mode (order started) */}
            {readOnly && (
              <Box
                padding="12px 24px"
                style={{ background: COLORS.tealBackground, borderBottom: `1px solid ${COLORS.tealPrimary}` }}
              >
                <Text.Caption color={COLORS.panther}>
                  Your order is in progress. Use this to estimate costs, but final items and pricing will be confirmed
                  by your Move & Pack team onsite.
                </Text.Caption>
              </Box>
            )}

            {/* Scrollable Content */}
            <ScrollableContent>
              {pricingInfoLoading || (debouncedSearchQuery && searchLoading) ? (
                <Box textAlign="center" padding="24px">
                  <Text.Body color={COLORS.storm}>
                    {debouncedSearchQuery ? 'Searching...' : 'Loading categories...'}
                  </Text.Body>
                </Box>
              ) : Object.keys(filteredCategories).length === 0 && debouncedSearchQuery ? (
                <Box textAlign="center" padding="24px">
                  <Text.Body color={COLORS.storm}>No categories found. Try a different search term.</Text.Body>
                </Box>
              ) : (
                Object.entries(filteredCategories).map(([groupKind, items]) => {
                  // Find the group metadata from backend to get icon
                  const groupMetadata = disposalCategoryGroups.find((g) => g.kind === groupKind);
                  const IconComponent = groupMetadata ? DISPOSAL_GROUP_ICONS[groupMetadata.kind] : undefined;
                  return (
                    <CategorySection key={groupKind}>
                      <CategoryHeader>
                        {IconComponent && <IconComponent />}
                        {groupMetadata?.name || groupKind}
                      </CategoryHeader>
                      <ItemTagsContainer>
                        {items.map((item) => {
                          const quantity = selectedItems.get(item.id)?.quantity || 0;
                          const selected = quantity > 0;
                          return (
                            <ItemTag key={item.id} selected={selected} onClick={() => handleTagClick(item)}>
                              <ItemName>{item.name}</ItemName>
                              {selected && (
                                <QuantityControls>
                                  <QuantityButton
                                    variant="remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveItem(item);
                                    }}
                                  >
                                    −
                                  </QuantityButton>
                                  <QuantityText>{quantity}</QuantityText>
                                  <QuantityButton
                                    variant="add"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddItem(item);
                                    }}
                                  >
                                    +
                                  </QuantityButton>
                                </QuantityControls>
                              )}
                            </ItemTag>
                          );
                        })}
                      </ItemTagsContainer>
                    </CategorySection>
                  );
                })
              )}
            </ScrollableContent>

            {/* Sticky Footer with Pricing + Actions */}
            <StickyFooter>
              {selectedItemsArray.length > 0 && (
                <CompactSummary>
                  <Box.Flex justifyContent="space-between" alignItems="center">
                    <div>
                      {pricingLoading ? (
                        <Text.Body weight={FontWeight.Medium} color={COLORS.storm} style={{ fontSize: '14px' }}>
                          Calculating...
                        </Text.Body>
                      ) : pricing ? (
                        <>
                          <Text.Body weight={FontWeight.Medium} color={COLORS.panther} style={{ fontSize: '14px' }}>
                            Est. Total: <Currency value={pricing.total} />
                          </Text.Body>
                          <Text.Caption color={COLORS.storm} style={{ fontSize: '11px', marginTop: '2px' }}>
                            {(() => {
                              const itemCount = selectedItemsArray.reduce((sum, i) => sum + i.quantity, 0);
                              return (
                                <>
                                  {itemCount} item{itemCount !== 1 ? 's' : ''} • <Currency value={pricing.flatFee} />{' '}
                                  flat fee + <Currency value={pricing.itemsTotal} />
                                </>
                              );
                            })()}
                          </Text.Caption>
                          {!readOnly && (
                            <Text.Caption
                              color={COLORS.storm}
                              style={{ fontSize: '11px', marginTop: '4px', fontStyle: 'italic' }}
                            >
                              Final pricing based on actual items picked up
                            </Text.Caption>
                          )}
                        </>
                      ) : null}
                    </div>
                  </Box.Flex>
                </CompactSummary>
              )}

              {/* Only show action buttons when NOT read-only */}
              {!readOnly && (
                <Box.Flex flexDirection="column" gap="12px" alignItems="center">
                  <Button
                    disabled={loading || selectedItemsArray.length === 0}
                    loading={attaching}
                    onClick={handleSubmit}
                    style={{ width: '100%' }}
                  >
                    {hasDisposal ? 'Save' : 'Add items'}
                  </Button>
                  {hasDisposal && (
                    <UnstyledButton
                      disabled={loading}
                      onClick={() => cancel()}
                      style={{
                        color: COLORS.toucan,
                        fontSize: '14px',
                        fontWeight: FontWeight.Medium,
                        cursor: canceling ? 'wait' : 'pointer',
                        opacity: canceling ? 0.6 : 1,
                      }}
                    >
                      {canceling ? 'Canceling...' : 'Cancel Disposal'}
                    </UnstyledButton>
                  )}
                </Box.Flex>
              )}

              {error && (
                <Text.Body color={COLORS.toucan} style={{ marginTop: '8px', textAlign: 'center', fontSize: '13px' }}>
                  Something went wrong. Please try again.
                </Text.Body>
              )}
            </StickyFooter>
          </>
        )}

        {tab === TAB_PRICING && (
          <ScrollableContent>
            <DisposalPricingSummary />
          </ScrollableContent>
        )}
        {tab === TAB_RESTRICTIONS && (
          <ScrollableContent>
            <RestrictedDisposalItems />
          </ScrollableContent>
        )}
      </ModalContainer>
    </Modal>
  );
}
